import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface AdminUser {
  userId: string
  empId: string
  fullName: string
  status: 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDED'
  lastLoginAt: string | null
  roles: string[]
  totpEnrolled: boolean
  /** Effective requirement — (tenant default AND role qualifies) OR the
   *  per-user override. See totpApi.ts for setting the override itself. */
  totpRequired: boolean
}

export interface Role {
  roleId: string
  code: string
  displayName: string
  description: string | null
  isSystem: boolean
}

export function useUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const res = await api.get<AdminUser[]>('/v1/users')
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export function useRoles() {
  return useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: async () => {
      const res = await api.get<Role[]>('/v1/roles')
      return res.data
    },
  })
}

export interface CreateUserRequest {
  empId: string
  fullName: string
  email?: string | null
  mobile?: string | null
  password: string
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateUserRequest) => {
      const res = await api.post<{ userId: string; empId: string; fullName: string }>(
        '/v1/users',
        body,
      )
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useSetUserStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; status: 'ACTIVE' | 'SUSPENDED' }) => {
      const res = await api.patch<AdminUser>(`/v1/users/${params.userId}/status`, {
        status: params.status,
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * Per-agent payment-mode restriction.
 *
 * `unrestricted: true` (an empty `modes`) means the agent may use every mode
 * in `allModes` — that is the default for anyone nobody has configured.
 */
export interface PaymentModes {
  modes: string[]
  allModes: string[]
  unrestricted: boolean
}

export function usePaymentModes(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'payment-modes'],
    enabled: !!userId,
    queryFn: async () => {
      const res = await api.get<PaymentModes>(`/v1/users/${userId}/payment-modes`)
      return res.data
    },
  })
}

export function useSetPaymentModes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; modes: string[] }) => {
      const res = await api.put<PaymentModes>(
        `/v1/users/${params.userId}/payment-modes`,
        { modes: params.modes },
      )
      return res.data
    },
    onSuccess: (_data: PaymentModes, vars: { userId: string; modes: string[] }) =>
      qc.invalidateQueries({ queryKey: ['admin', 'users', vars.userId, 'payment-modes'] }),
  })
}

export function useGrantRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; roleCode: string; scopeNodeId?: string }) => {
      await api.post(`/v1/users/${params.userId}/roles`, {
        roleCode: params.roleCode,
        ...(params.scopeNodeId ? { scopeNodeId: params.scopeNodeId } : {}),
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

export function useRevokeRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; roleCode: string }) => {
      await api.delete(`/v1/users/${params.userId}/roles/${params.roleCode}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * Offboard a user: revokes every ACTIVE allocation they hold and flips
 * status to OFFBOARDED. Undone by {@link useReactivateUser}.
 */
export function useOffboardUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string }) => {
      const res = await api.post<{ userId: string; revokedAllocations: number }>(
        `/v1/users/${params.userId}/offboard`,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
      qc.invalidateQueries({ queryKey: ['allocations'] })
    },
  })
}

/**
 * Bring an OFFBOARDED user back as ACTIVE, same Emp ID and roles. Allocations
 * revoked at offboarding are not restored. 409 when the user isn't offboarded.
 */
export function useReactivateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string }) => {
      const res = await api.post<AdminUser>(`/v1/users/${params.userId}/reactivate`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/** One user with email and mobile — the listing leaves contact details out. */
export interface UserDetails {
  userId: string
  empId: string
  fullName: string
  email: string | null
  mobile: string | null
  status: 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDED'
  lastLoginAt: string | null
}

export function useUserDetails(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'details'],
    enabled: !!userId,
    queryFn: async () => (await api.get<UserDetails>(`/v1/users/${userId}`)).data,
  })
}

/** Omitted fields stay as they are; an empty email or mobile clears it. */
export interface UserUpdate {
  empId?: string
  fullName?: string
  email?: string
  mobile?: string
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; change: UserUpdate }) =>
      (await api.patch<UserDetails>(`/v1/users/${params.userId}`, params.change)).data,
    onSuccess: (data: UserDetails) => {
      qc.setQueryData(['admin', 'users', data.userId, 'details'], data)
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/** The signed-in user's own details, for Profile. */
export function useMyProfile() {
  return useQuery({
    queryKey: ['me', 'profile'],
    queryFn: async () => (await api.get<UserDetails>('/v1/me/profile')).data,
  })
}
